import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { motion } from "framer-motion";
import { Link as ScrollLink } from "react-scroll";
import { Link as RouterLink, useLocation } from "react-router-dom";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", to: "home", type: "scroll" },
    { name: "About", to: "about", type: "scroll" },
    { name: "Services", to: "services", type: "scroll" },
    { name: "Case Studies", to: "case-studies", type: "scroll" },
    { name: "Testimonials", to: "testimonials", type: "scroll" },
    { name: "Contact", to: "contact", type: "scroll" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0f111a]/90 backdrop-blur-md shadow-md"
          : "bg-transparent backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="cursor-pointer flex items-center"
        >
          <RouterLink to="/">
            <img src="/logo_02_01.png" alt="Logo" className="h-10 md:h-12 w-auto" />
          </RouterLink>
        </motion.div>

        {/* Desktop Menu */}
        <ul className="hidden md:flex space-x-8 font-medium">
          {navItems.map((item) => (
            <li key={item.name} className="relative group">
              {location.pathname === "/" && item.type === "scroll" ? (
                <ScrollLink
                  to={item.to}
                  smooth={true}
                  duration={500}
                  offset={-70}
                  className="cursor-pointer text-gray-200 hover:text-white transition-colors duration-300"
                >
                  {item.name}
                </ScrollLink>
              ) : (
                <RouterLink
                  to={`/#${item.to}`}
                  className="cursor-pointer text-gray-200 hover:text-white transition-colors duration-300"
                >
                  {item.name}
                </RouterLink>
              )}
              <span className="absolute bottom-[-2px] left-0 w-0 h-[2px] bg-gradient-to-r from-[#00fff7] to-[#0077ff] transition-all duration-300 group-hover:w-full"></span>
            </li>
          ))}
        </ul>

        {/* Mobile Menu */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle navigation"
          className="md:hidden p-2 rounded-md hover:bg-white/20 transition-colors duration-200"
        >
          {isOpen ? <X size={24} className="text-white" /> : <Menu size={24} className="text-white" />}
        </button>
      </div>

      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.4 }}
        className="md:hidden overflow-hidden"
      >
        <ul className="flex flex-col items-center py-4 space-y-3 bg-[#0f111a]/95 font-medium backdrop-blur-md shadow-lg">
          {navItems.map((item) => (
            <li key={item.name} className="w-full text-center">
              {location.pathname === "/" && item.type === "scroll" ? (
                <ScrollLink
                  to={item.to}
                  smooth={true}
                  duration={500}
                  offset={-70}
                  onClick={() => setIsOpen(false)}
                  className="block py-2 text-gray-200 hover:text-white transition-colors duration-200 cursor-pointer"
                >
                  {item.name}
                </ScrollLink>
              ) : (
                <RouterLink
                  to={`/#${item.to}`}
                  onClick={() => setIsOpen(false)}
                  className="block py-2 text-gray-200 hover:text-white transition-colors duration-200 cursor-pointer"
                >
                  {item.name}
                </RouterLink>
              )}
            </li>
          ))}
        </ul>
      </motion.div>
    </nav>
  );
}
